import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription } from "@/components/ui/empty";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import type { ClioMatterListItem } from "./types";

interface MatterListProps {
	items: ClioMatterListItem[];
	selectedClioId: number | null;
	onSelect: (clioId: number) => void;
	isLoading: boolean;
	onLoadMore?: () => void;
	canLoadMore?: boolean;
}

const STATUS_COLORS: Record<"Open" | "Pending" | "Closed", string> = {
	Open: "bg-success/15 text-success",
	Pending: "bg-warning/15 text-warning",
	Closed: "bg-muted text-muted-foreground",
};

export function MatterList({
	items,
	selectedClioId,
	onSelect,
	isLoading,
	onLoadMore,
	canLoadMore,
}: MatterListProps) {
	if (isLoading && items.length === 0) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>Loading matters…</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}
	if (items.length === 0) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>
						No matters yet. Click Sync now to pull from Clio.
					</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}
	return (
		<ScrollArea className="h-full min-h-0 flex-1">
			<div className="flex flex-col divide-y">
				{items.map((item) => {
					const isSelected = selectedClioId === item.clioId;
					return (
						<Button
							key={item._id}
							variant="ghost"
							aria-pressed={isSelected}
							onClick={() => onSelect(item.clioId)}
							className={cn(
								"flex h-auto flex-col items-start gap-1 rounded-none px-4 py-3 text-left text-sm whitespace-normal",
								isSelected && "bg-muted",
							)}
						>
							<div className="flex w-full items-center gap-2">
								<span className="font-semibold truncate">
									{item.displayNumber || `Matter ${item.clioId}`}
								</span>
								<Badge className={cn("ml-auto", STATUS_COLORS[item.status])}>
									{item.status}
								</Badge>
							</div>
							<div className="line-clamp-2 text-xs font-normal text-muted-foreground">
								{item.description ?? "—"}
							</div>
							<div className="flex w-full flex-wrap items-center gap-2 text-xs font-normal text-muted-foreground">
								{item.clientName && <span>{item.clientName}</span>}
								{item.practiceAreaName && (
									<Badge variant="outline" className="font-normal">
										{item.practiceAreaName}
									</Badge>
								)}
								{item.matterStage && (
									<Badge variant="outline" className="font-normal">
										{item.matterStage}
									</Badge>
								)}
								{item.openDate && (
									<span className="ml-auto text-[11px]">
										Opened {item.openDate}
									</span>
								)}
							</div>
						</Button>
					);
				})}
				{canLoadMore && (
					<div className="p-3">
						<Button
							variant="outline"
							size="sm"
							className="w-full"
							onClick={onLoadMore}
						>
							Load more
						</Button>
					</div>
				)}
			</div>
		</ScrollArea>
	);
}
